import React from 'react';
import loadable from '@loadable/component';
import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { propTypes } from '../../util/types';
import { H1 } from '../PageBuilder/Primitives/Heading';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import logoNewImage from '../../assets/images/logo_new.png';
import hensImg from '../../assets/images/hens.jpeg';
import css from './TermsOfServicePage.module.css';
import { Page } from '../../components';
import FallbackPage, { fallbackSections } from './FallbackPage';
import { ASSET_NAME } from './TermsOfServicePage.duck';
import { camelize } from '../../util/string';
import { getMetadata } from '../PageBuilder/PageBuilder';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import gettingPaidFooter from '../../assets/images/gettingPaidFooter.png';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);
const SectionBuilder = loadable(
  () => import(/* webpackChunkName: "SectionBuilder" */ '../PageBuilder/PageBuilder'),
  {
    resolveComponent: components => components.SectionBuilder,
  }
);

// This "content-only" component can be used in modals etc.
const TermsOfServiceContent = props => {
  const { inProgress, error, data } = props;

  // We don't want to add h1 heading twice to the HTML (SEO issue).
  // Modal's header is mapped as h2
  const hasContent = data => typeof data?.content === 'string';
  const exposeContentAsChildren = data => {
    return hasContent(data) ? { children: data.content } : {};
  };

  if (!hasContent && inProgress) {
    return null;
  }

  const CustomHeading1 = props => <H1 as="h2" {...props} />;

  const hasData = error === null && data;
  const sectionsData = hasData ? data : fallbackSections;

  return (
    <SectionBuilder
      {...sectionsData}
      options={{
        fieldComponents: {
          heading1: { component: CustomHeading1, pickValidProps: exposeContentAsChildren },
        },
        isInsideContainer: true,
      }}
    />
  );
};

// Presentational component for TermsOfServicePage
const TermsOfServicePageComponent = props => {
  // const { pageAssetsData, inProgress, error } = props;
  const { pageAssetsData } = props;
  const { meta = {} } = pageAssetsData?.[camelize(ASSET_NAME)]?.data || {};
  const pageMetaProps = getMetadata(meta, 'Article');

  return (
    <>
      {/*<PageBuilder
        pageAssetsData={pageAssetsData?.[camelize(ASSET_NAME)]?.data}
        inProgress={inProgress}
        error={error}
        fallbackPage={<FallbackPage />}
      />*/}
      <Page {...pageMetaProps} scrollingDisabled={props.scrollingDisabled}>
        <div 
          className={css.TermsOfServicePage}
          style={{
            backgroundImage: `url(${require('../../assets/images/woodBg.jpg')})`,
          }}
        >
          <TopbarContainer />

          {/* Hero Section */}
          <div
            className={css.heroSection}
            style={{
              backgroundImage: `url(${require('../../assets/images/termsOfService.png')})`,
            }}
          >
            <div className={css.heroContainer}>
              <img src={logoNewImage} alt="Farmiez Logo" className={css.faqLogo} />
              <h1 className={css.heroTitle}>Terms of Service</h1>
            </div>
          </div>

          <div 
            className={css.aboutContentWrap}
            // style={{
            //   backgroundImage: `url(${require('../../assets/images/woodBg.jpg')})`,
            // }}
          >
            {/* Content Section */}
            <div className={css.contentSection}>
              <div className={css.textBlock}>
                <h3 >1. Interpretation and Definitions</h3>
                <ul className={css.faqList}>
                  <li><strong>1.1 Interpretation</strong><br />
                    Words with an initial capitalized letter have specific meanings defined below. These definitions apply regardless of singular or plural usage.
                  </li>
                  <li><strong>1.2 Definitions</strong><br />
                    For these Terms of Service:
                  </li>
                  <li>
                    <ul>
                      <li><strong>Account </strong>means a unique account created by You to access the Service.</li>
                      <li><strong>Affiliate </strong>means any entity controlling, controlled by, or under common control with a party (50% or more ownership).</li>
                      <li><strong>Application </strong>refers to the software platform provided by Farmiez Marketplace.</li>
                      <li><strong>Farmiez Shopper </strong>refers to users placing Orders for Goods.</li>
                      <li><strong>Country </strong>refers to the United States.</li>
                      <li><strong>Company </strong>(also "We", "Us", or "Our") refers to <b>Farmiez Marketplace, 2129 General Booth Blvd, Suite 103-283, Virginia Beach, VA 23454</b>.</li>
                      <li><strong>Cookies </strong>are small files that are placed on Your computer, mobile device or any other device by a website, containing the details of Your browsing history on that website among its many uses.</li>
                      <li><strong>Content </strong>includes text, images, or other material posted or uploaded by You.</li>
                      <li><strong>Device</strong> means any device accessing our Service (e.g., computers, smartphones, tablets).</li>
                      <li><strong>Feedback </strong>refers to any suggestions or improvements submitted by You.</li>
                      <li><strong>Good(s) </strong>refers to items offered for sale on the Service.</li>
                      <li><strong>Farmiez Farmer </strong>refers to users listing Goods for sale.</li>
                      <li><strong>Service </strong>refers to our Application, Website, or both.</li>
                      <li><strong>Terms of Service (Terms) </strong>mean this agreement between You and Farmiez Marketplace.</li>
                      <li><strong>Third-party Social Media Service </strong>means external services or content linked or included in our Service.</li>
                      <li><strong>Website </strong>refers explicitly to Farmiez Marketplace, accessible from <b>www.farmiez.com</b>.</li>
                      <li><strong>You </strong>means any individual or entity accessing or using our Service.</li>
                    </ul>
                  </li>
                </ul>

                <h3 className={css.subHeading}>2. Contact Us</h3>
                <ul className={css.faqList}>
                  <li>For questions about these Terms of Service, contact us via:</li>
                  <li>
                    <ul>
                      <li><strong>Email:</strong> hello@farmiezmarket.com</li>
                      <li><strong>Website:</strong> www.farmiez.com</li>
                    </ul>
                  </li>
                </ul>

                <h3 className={css.subHeading}>3. Acknowledgment</h3>
                <ul className={css.faqList}>
                  <li>By accessing or using our Service, You accept these Terms fully. If You disagree, do not use our Service.</li>
                  <li>You affirm You are of legal age in your jurisdiction. Those below legal age are prohibited from using this Service.</li>
                  <li>Use of our Service also means accepting our Privacy Policy detailing data collection and use.</li>
                </ul>

                <h3 className={css.subHeading}>4. Governing Law</h3>
                <ul className={css.faqList}>
                  <li>These Terms are governed by the laws of the United States, excluding conflict-of-law rules. Your use may also be subject to local or national laws.</li>
                  <li>
                    <ul>
                      <li><strong>4.1 United States Legal Compliance</strong><br />You represent and warrant that You are not located in a sanctioned or embargoed country and are not listed on any U.S. government restricted lists.</li>
                      <li><strong>4.2 Severability</strong><br />If any provision is deemed invalid or unenforceable, it will be adjusted or interpreted accordingly, with other provisions remaining in effect.</li>
                      <li><strong>4.3 Waiver</strong><br />Failure to enforce rights or obligations does not constitute a waiver of future enforcement.</li>
                    </ul>
                  </li>
                </ul>

                <h3 className={css.subHeading}>5. User Accounts</h3>
                <ul className={css.faqList}>
                  <li><strong>5.1 Account Creation</strong><br />Provide accurate, complete, and current information. Failure may result in account termination. Do not use unauthorized or offensive usernames.</li>
                  <li><strong>5.2 Account Information</strong><br />Provide relevant account details including name, email, phone, address, and identity documents as requested.</li>
                  <li><strong>5.3 Account Review</strong><br />We do not conduct user background checks and cannot ensure accuracy of user-provided information.</li>
                  <li><strong>5.4 Account Password</strong><br />Secure Your password. Immediately report any unauthorized use or security breach.</li>
                  <li><strong>5.5 Account Termination</strong><br />We may terminate or suspend Your Account without notice for violations of these Terms. To terminate, simply stop using or delete Your Account.</li>
                </ul>

                <h3 className={css.subHeading}>6. Content</h3>
                <ul className={css.faqList}>
                  <li><strong>6.1 Your Right to Post Content</strong><br />
                    You retain ownership but grant us rights to use, modify, and display content. You affirm You have necessary rights to your posted content.
                  </li>
                  <li><strong>6.2 Content Restrictions</strong><br />
                    You must not post content that's illegal, defamatory, discriminatory, fraudulent, obscene, or harmful. We may remove inappropriate content and restrict your access.
                  </li>
                  <li><strong>6.3 Content Backups</strong><br />
                    We regularly backup content but cannot guarantee data integrity. Maintain independent copies.
                  </li>
                  <li><strong>6.4 Intellectual Property and Copyright</strong><br />
                    Respect others’ copyrights. We respond to infringement claims per applicable law.
                  </li>
                </ul>

                <h3 className={css.subHeading}>7. Orders of Goods</h3>
                <ul className={css.faqList}>
                  <li>By ordering, You affirm legal capability to enter binding contracts.</li>
                  <li><strong>7.1 Role of the Service</strong><br />
                    We facilitate connections between Farmiez Shoppers and Farmiez Farmers but are not party to agreements between them.
                  </li>
                  <li><strong>7.2 Your Information as Farmiez Shopper</strong><br />
                    You must provide accurate information (name, contact, payment details) when placing orders.
                  </li>
                  <li><strong>7.3 Availability, Errors, and Pricing</strong><br />
                    We do not guarantee accuracy or availability of goods. Prices or details may change without notice.
                  </li>
                  <li><strong>7.4 Pricing Policy</strong><br />
                    Farmiez Marketplace and Farmers may revise prices prior to acceptance. You can cancel orders impacted by pricing changes.
                  </li>
                  <li><strong>7.5 Payments</strong><br />
                    Payments processed securely via Stripe. You must use valid payment methods. Authorization issues may delay or cancel orders.
                  </li>
                  <li><strong>7.6 Service Fees</strong><br />
                    Farmiez Marketplace charges a 10% commission fee explicitly to Farmiez Farmers on each completed transaction. Fees subject to change upon notice.
                  </li>
                  <li><strong>7.7 Order Modifications & Cancellations</strong><br />
                    Farmers and Shoppers must manage modifications.
                  </li>
                  <li>
                    <ul>
                      <li>If a Shopper cancels, payments are non-refundable unless special circumstances apply (contact us).</li>
                      <li>If a Farmer cancels, Shoppers receive full refunds.</li>
                    </ul>
                  </li>
                  <li><strong>7.8 Order Disputes</strong><br />
                    Report disputes to us promptly; resolutions are at our discretion.
                      </li>
                </ul>

                <h3 className={css.subHeading}>8. Disclaimer and Limitation of Liability</h3>
                <ul className={css.faqList}>
                  <li>Our Service is provided "AS IS," without warranties. We disclaim liability for indirect or consequential damages. Maximum liability is the amount you paid via the Service, or $100 USD.</li>
                  <li><strong>8.1 Links to External Sites</strong><br />
                    We do not control external websites or services. Use at your own risk.
                  </li>
                  <li><strong>8.2 Translations</strong><br />
                    English Terms prevail in any discrepancies.
                  </li>
                </ul>

                <h3 className={css.subHeading}>9. Disputes Resolution</h3>
                <ul className={css.faqList}>
                  <li>Attempt informal resolution by contacting us directly.</li>
                </ul>

                <h3 className={css.subHeading}>10. Intellectual Property</h3>
                <ul className={css.faqList}>
                  <li>Our content, trademarks, and trade dress remain exclusively our property. Use without permission is prohibited.</li>
                </ul>

                <h3 className={css.subHeading}>12. Changes to these Terms</h3>
                <ul className={css.faqList}>
                  <li>We reserve the right to update these Terms with 30 days’ notice. Continued use after changes implies acceptance.</li>
                </ul>

                <h3 className={css.subHeading}>Additional Important Information:</h3>
                <ul className={css.faqList}>
                  <li><strong>Farmiez Marketplace</strong> must ensure compliance with local health, safety, and regulatory standards for selling fresh eggs. Farmiez Marketplace facilitates transactions but is not responsible for product quality or regulatory compliance.</li>
                </ul>
              </div>
            </div>

            <img src={gettingPaidFooter} alt="img" className={css.footerImg} />
          </div>
          <FooterContainer />
        </div>
      </Page>
    </>
  );
};

TermsOfServicePageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  return { pageAssetsData, inProgress, error };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const TermsOfServicePage = compose(connect(mapStateToProps))(TermsOfServicePageComponent);

const TOS_ASSET_NAME = ASSET_NAME;
export { TOS_ASSET_NAME, TermsOfServicePageComponent, TermsOfServiceContent };

export default TermsOfServicePage;
